import { useState } from "react";
import { useProjects } from "../../hooks/useProjects.js";
import { useProfiles } from "../../hooks/useProfiles.js";
import { useCreateProject } from "../../hooks/useMutations.js";
import { ProjectCard } from "../../components/ProjectCard/ProjectCard.js";
import { Skeleton } from "../../components/Skeleton/Skeleton.js";
import { Modal } from "../../components/Modal/Modal.js";
import styles from "./Projects.module.css";

export function ProjectsPage() {
  const { data, isLoading, error, refetch } = useProjects();
  const { data: profilesData } = useProfiles();
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  // Create form state
  const [newName, setNewName] = useState("");
  const [newUrlBase, setNewUrlBase] = useState("https://");
  const [newProfile, setNewProfile] = useState("");
  const [newSchedule, setNewSchedule] = useState("0 */6 * * *");
  const [newActive, setNewActive] = useState(true);
  const [newPortada, setNewPortada] = useState("/");

  const createProject = useCreateProject();

  const projects = (data?.data ?? []).filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()),
  );

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    createProject.mutate(
      {
        name: newName,
        url_base: newUrlBase,
        profile: newProfile,
        schedule: newSchedule || null,
        active: newActive,
        url_types: { portada: { url: newPortada } },
        overrides: {},
      },
      {
        onSuccess: () => {
          setShowCreate(false);
          setNewName("");
          setNewUrlBase("https://");
          setNewProfile("");
          setNewSchedule("0 */6 * * *");
          setNewActive(true);
          setNewPortada("/");
        },
      },
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.heading}>Projects</h1>
        <div className={styles.headerRight}>
          <input
            type="search"
            placeholder="Filter projects…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.search}
          />
          <button className={styles.newBtn} onClick={() => setShowCreate(true)}>
            + New Project
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          Failed to load projects.{" "}
          <button onClick={() => void refetch()}>Retry</button>
        </div>
      )}

      {isLoading ? (
        <div className={styles.grid}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} height="180px" borderRadius="8px" />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className={styles.empty}>
          {search ? `No projects matching "${search}"` : "No projects yet."}
        </div>
      ) : (
        <div className={styles.grid}>
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      )}

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="New Project">
        <form onSubmit={handleCreate} className={styles.form}>
          <div className={styles.formField}>
            <label htmlFor="proj-name">Name</label>
            <input
              id="proj-name"
              type="text"
              required
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className={styles.input}
              placeholder="My Project"
            />
          </div>
          <div className={styles.formField}>
            <label htmlFor="proj-url">URL base</label>
            <input
              id="proj-url"
              type="url"
              required
              pattern="https://.*"
              value={newUrlBase}
              onChange={(e) => setNewUrlBase(e.target.value)}
              className={styles.input}
              placeholder="https://example.com"
            />
          </div>
          <div className={styles.formField}>
            <label htmlFor="proj-profile">Profile</label>
            <select
              id="proj-profile"
              required
              value={newProfile}
              onChange={(e) => setNewProfile(e.target.value)}
              className={styles.input}
            >
              <option value="" disabled>Select a profile…</option>
              {(profilesData ?? []).map((p) => (
                <option key={p.slug} value={p.slug}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className={styles.formField}>
            <label htmlFor="proj-schedule">Schedule <span style={{ color: "#999", fontWeight: "normal" }}>(optional)</span></label>
            <input
              id="proj-schedule"
              type="text"
              value={newSchedule}
              onChange={(e) => setNewSchedule(e.target.value)}
              className={styles.input}
              placeholder="0 */6 * * *  — leave blank to disable"
            />
          </div>
          <div className={styles.formField}>
            <label htmlFor="proj-portada">Portada URL</label>
            <input
              id="proj-portada"
              type="text"
              required
              value={newPortada}
              onChange={(e) => setNewPortada(e.target.value)}
              className={styles.input}
              placeholder="/"
            />
          </div>
          <div className={styles.formCheck}>
            <input
              id="proj-active"
              type="checkbox"
              checked={newActive}
              onChange={(e) => setNewActive(e.target.checked)}
            />
            <label htmlFor="proj-active">Active</label>
          </div>
          <button
            type="submit"
            className={styles.submitBtn}
            disabled={createProject.isPending}
          >
            {createProject.isPending ? "Creating…" : "Create"}
          </button>
        </form>
      </Modal>
    </div>
  );
}
